import { useState } from 'react';
import { Box, Center, Text, VStack, useColorModeValue, HStack, Code } from '@chakra-ui/react';
import ReactCountryFlag from 'react-country-flag';
import SelectCountry from 'components/ui/SelectCountry';
import { Country } from 'components/ui/countries_data';

const SCountryPreview = () => {
    const [selectedSmall, setSelectedSmall] = useState<Country | null>(null);
    const [selectedMedium, setSelectedMedium] = useState<Country | null>(null);
    const [selectedLarge, setSelectedLarge] = useState<Country | null>(null);
    const [selectedCustom, setSelectedCustom] = useState<Country | null>(null);
    const [selectedWidth, setSelectedWidth] = useState<Country | null>(null);

    const bg = useColorModeValue('gray.50', 'gray.900');
    const textColor = useColorModeValue('gray.800', 'white');
    const codeBg = useColorModeValue('gray.100', 'gray.800');

    return (
        <Center minH="100vh" bg={bg} p="40px">
            <VStack spacing="56px" w="100%" maxW="900px">
                {/* Header */}
                <VStack spacing="8px" align="flex-start" w="100%">
                    <Text fontSize="2xl" fontWeight="700" color={textColor}>
                        SelectCountry — Preview
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        Componente para seleccionar países con código telefónico
                    </Text>
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 1: Tamaño Small */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        📱 Tamaño Small (sm)
                    </Text>
                    <Box w="100%" maxW="280px">
                        <SelectCountry
                            label="País"
                            placeholder="Selecciona..."
                            onSelect={setSelectedSmall}
                            size="sm"
                            dropdownPosition='bottom'
                            dropdownOffset={30}
                            triggerWidth="60px"
                            dropdownWidth="350px"
                            flagOnly={true}
                        />
                    </Box>
                    {selectedSmall && (
                        <VStack spacing="8px" align="flex-start" w="100%" bg={codeBg} p="12px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600">
                                Seleccionado:
                            </Text>
                            <HStack spacing="2" fontSize="sm">
                                {selectedSmall && (
                                    <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px">
                                        <ReactCountryFlag countryCode={selectedSmall.iso} svg style={{ width: '16px', height: '16px', borderRadius: '2px', display: 'block' }} />
                                    </Box>
                                )}
                                <Code>{selectedSmall.country}</Code>
                                <Code>{selectedSmall.code}</Code>
                            </HStack>
                        </VStack>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 2: Tamaño Medium (default) */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        🔷 Tamaño Medium (md) - Default
                    </Text>
                    <Box w="100%" maxW="400px">
                        <SelectCountry
                            label="Selecciona tu país"
                            placeholder="Busca un país..."
                            onSelect={setSelectedMedium}
                            size="md"
                            dropdownPosition='bottom'
                        />
                    </Box>
                    {selectedMedium && (
                        <VStack spacing="8px" align="flex-start" w="100%" bg={codeBg} p="14px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600">
                                Seleccionado:
                            </Text>
                            <HStack spacing="2" fontSize="sm">
                                {selectedMedium && (
                                    <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px">
                                        <ReactCountryFlag countryCode={selectedMedium.iso} svg style={{ width: '16px', height: '16px', borderRadius: '2px', display: 'block' }} />
                                    </Box>
                                )}
                                <Code>{selectedMedium.country}</Code>
                                <Code colorScheme="blue">{selectedMedium.code}</Code>
                                <Code colorScheme="green">{selectedMedium.iso}</Code>
                            </HStack>
                        </VStack>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 3: Tamaño Large */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        📲 Tamaño Large (lg)
                    </Text>
                    <Box w="100%">
                        <SelectCountry
                            label="Selecciona tu país de residencia"
                            placeholder="Busca entre 70+ países..."
                            onSelect={setSelectedLarge}
                            size="lg"
                            dropdownPosition='top'

                        />
                    </Box>
                    {selectedLarge && (
                        <VStack spacing="12px" align="flex-start" w="100%" bg={codeBg} p="16px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600">
                                País seleccionado:
                            </Text>
                            <VStack spacing="6px" align="flex-start" w="100%">
                                <HStack spacing="3">
                                    {selectedLarge && (
                                        <Box boxShadow="0 2px 8px rgba(0,0,0,0.15)" borderRadius="3px">
                                            <ReactCountryFlag countryCode={selectedLarge.iso} svg style={{ width: '28px', height: '28px', borderRadius: '3px', display: 'block' }} />
                                        </Box>
                                    )}
                                    <VStack spacing="0" align="start">
                                        <Text fontSize="md" fontWeight="600">
                                            {selectedLarge.country}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            Código: <Code>{selectedLarge.code}</Code> | ISO: <Code>{selectedLarge.iso}</Code>
                                        </Text>
                                    </VStack>
                                </HStack>
                            </VStack>
                        </VStack>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 4: Solo bandera (flagOnly) */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        🚩 Solo Bandera (flagOnly)
                    </Text>
                    <Box w="100%">
                        <SelectCountry
                            label="Selecciona país (solo bandera)"
                            placeholder="Elige..."
                            onSelect={setSelectedCustom}
                            size="lg"
                            flagOnly={true}
                        />
                    </Box>
                    {selectedCustom && (
                        <Box w="100%" bg={codeBg} p="14px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600" mb="8px">
                                País seleccionado:
                            </Text>
                            <HStack spacing="2" fontSize="sm">
                                {selectedCustom && (
                                    <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px">
                                        <ReactCountryFlag countryCode={selectedCustom.iso} svg style={{ width: '20px', height: '20px', borderRadius: '2px', display: 'block' }} />
                                    </Box>
                                )}
                                <Code>{selectedCustom.country}</Code>
                                <Code colorScheme="blue">{selectedCustom.code}</Code>
                            </HStack>
                        </Box>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 5: Anchos independientes */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        📐 Anchos Independientes (trigger más estrecho que dropdown)
                    </Text>
                    <HStack spacing="4" align="flex-start" w="100%">
                        <VStack spacing="4" align="flex-start" flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.600">
                                Input: 200px
                            </Text>
                            <SelectCountry
                                label="Selecciona país"
                                placeholder="Busca..."
                                onSelect={setSelectedWidth}
                                size="md"
                                triggerWidth="200px"
                                dropdownWidth="350px"
                            />
                        </VStack>
                        <Box flex={1} fontSize="xs" color="gray.500">
                            <Text mb="2">Trigger width: 200px</Text>
                            <Text>Dropdown width: 350px</Text>
                        </Box>
                    </HStack>
                    {selectedWidth && (
                        <Box w="100%" bg={codeBg} p="12px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600" mb="8px">
                                País seleccionado:
                            </Text>
                            <HStack spacing="2" fontSize="sm">
                                {selectedWidth && (
                                    <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px">
                                        <ReactCountryFlag countryCode={selectedWidth.iso} svg style={{ width: '16px', height: '16px', borderRadius: '2px', display: 'block' }} />
                                    </Box>
                                )}
                                <Code>{selectedWidth.country}</Code>
                                <Code colorScheme="blue">{selectedWidth.code}</Code>
                            </HStack>
                        </Box>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Ejemplo 6: Con border radius personalizado */}
                <VStack spacing="16px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                        🎨 Con Border Radius personalizado (lg)
                    </Text>
                    <Box w="100%" maxW="420px">
                        <SelectCountry
                            label="País con bordes redondeados"
                            placeholder="Busca..."
                            onSelect={setSelectedCustom}
                            size="md"
                            borderRadius="16px"
                        />
                    </Box>
                    {selectedCustom && (
                        <Box w="100%" bg={codeBg} p="14px" borderRadius="md">
                            <Text fontSize="xs" fontWeight="600" color="gray.600" mb="8px">
                                Datos completos:
                            </Text>
                            <VStack spacing="4px" align="flex-start" fontSize="sm" fontFamily="mono">
                                <Text>país: <Code colorScheme="blue">{selectedCustom.country}</Code></Text>
                                <Text>código: <Code colorScheme="green">{selectedCustom.code}</Code></Text>
                                <Text>iso: <Code colorScheme="orange">{selectedCustom.iso}</Code></Text>
                            </VStack>
                        </Box>
                    )}
                </VStack>

                <Box w="100%" h="1px" bg="gray.200" />

                {/* Props Documentation */}
                <VStack spacing="12px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="600" color="gray.600">
                        📋 Props disponibles:
                    </Text>
                    <VStack spacing="6px" align="flex-start" fontSize="xs" color="gray.500" w="100%">
                        <Text>• <Code>onSelect</Code>: Callback que retorna el objeto Country</Text>
                        <Text>• <Code>label</Code>: Etiqueta del select (opcional)</Text>
                        <Text>• <Code>placeholder</Code>: Texto placeholder (default: "Select a country")</Text>
                        <Text>• <Code>value</Code>: ISO code del país pre-seleccionado (opcional)</Text>
                        <Text>• <Code>size</Code>: Tamaño: "sm" | "md" | "lg" (default: "md")</Text>
                        <Text>• <Code>borderRadius</Code>: Radio de bordes (default: "md")</Text>
                        <Text>• <Code>dropdownPosition</Code>: Posición del dropdown: "top" | "bottom" (default: "bottom")</Text>
                        <Text>• <Code>dropdownOffset</Code>: Gap entre trigger y dropdown en px (default: 6)</Text>
                        <Text>• <Code>w</Code>: Ancho del componente (default: "100%")</Text>
                        <Text>• <Code>flagOnly</Code>: Mostrar solo bandera cuando está seleccionado (default: false)</Text>
                        <Text>• <Code>triggerWidth</Code>: Ancho del trigger/input (default: hereda de w)</Text>
                        <Text>• <Code>dropdownWidth</Code>: Ancho del dropdown (default: hereda de w)</Text>
                    </VStack>
                </VStack>

                <VStack spacing="12px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="600" color="gray.600">
                        📦 Country Object:
                    </Text>
                    <Box bg={codeBg} p="12px" borderRadius="md" w="100%" fontSize="xs" fontFamily="mono" overflowX="auto">
                        <Text>{`{
  country: string;   // "Mexico"
  code: string;      // "+52"
  iso: string;       // "MX"
  flag: string;      // (deprecated - emoji, now using SVG flags)
}`}</Text>
                    </Box>
                </VStack>

                <VStack spacing="12px" align="flex-start" w="100%">
                    <Text fontSize="sm" fontWeight="600" color="gray.600">
                        ✨ Características:
                    </Text>
                    <VStack spacing="6px" align="flex-start" fontSize="xs" color="gray.500" w="100%">
                        <Text>✓ Búsqueda en tiempo real (país, código, ISO)</Text>
                        <Text>✓ 70+ países con banderas SVG</Text>
                        <Text>✓ Tamaños responsivos (sm, md, lg)</Text>
                        <Text>✓ Soporte dark/light mode</Text>
                        <Text>✓ Dropdown posicionable (arriba/abajo)</Text>
                        <Text>✓ Click outside detection</Text>
                        <Text>✓ Scroll personalizado</Text>
                        <Text>✓ Border radius personalizable</Text>
                    </VStack>
                </VStack>
            </VStack>
        </Center>
    );
};

export default SCountryPreview;